import type { Message } from '../api/types'
import type { OutboxRow } from '../db/db'
import { attachmentKind } from '../lib/files'

/** A message as rendered in the conversation: either confirmed or still in the outbox. */
export interface TimelineMessage {
  message: Message
  pending: OutboxRow | null
}

export function outboxRowToMessage(row: OutboxRow, meId: string): Message {
  return {
    id: row.id,
    channelId: row.channelId,
    seq: Number.POSITIVE_INFINITY,
    authorId: meId,
    body: row.body,
    createdAt: row.createdAt,
    editedAt: null,
    deletedAt: null,
    replyToId: row.replyToId,
    context: row.context ?? null,
    answer: row.answer ?? null,
    reactions: [],
    attachments: row.attachments.map((a) => ({
      ...a,
      status: 'ready' as const,
      blurhash: null,
      hasThumb: false,
      kind: a.kind ?? attachmentKind(a.contentType),
      voice: a.voice ?? false,
      durationMs: a.durationMs ?? null,
      // Not converted until it is sent.
      media: null,
      waveform: a.waveform ?? null,
    })),
  }
}

/**
 * Confirmed messages ordered by seq, followed by outbox rows (not yet confirmed) ordered by
 * creation time then id (UUIDv7 ids are time ordered). An outbox row whose id already exists as a
 * confirmed message is hidden so the message never renders twice.
 */
export function mergeOutbox(messages: Message[], outbox: OutboxRow[], meId: string): TimelineMessage[] {
  const confirmed = [...messages].sort((a, b) => a.seq - b.seq)
  const ids = new Set(confirmed.map((m) => m.id))
  const pending = outbox
    .filter((row) => !ids.has(row.id))
    .sort((a, b) => (a.createdAt === b.createdAt ? (a.id < b.id ? -1 : a.id > b.id ? 1 : 0) : a.createdAt < b.createdAt ? -1 : 1))
  return [
    ...confirmed.map((message) => ({ message, pending: null })),
    ...pending.map((row) => ({ message: outboxRowToMessage(row, meId), pending: row })),
  ]
}
